/**
 * `/v/{room}` 뷰어 — §14 "자막 전용(발표 모드)".
 *
 * 회의실 프로젝터 용례: 최신 자막이 **스크롤 없이** 보여야 한다(CaptionFeed
 * 하단 고정). 위로 끌면 히스토리를 지연 로드한다(§8.3).
 *
 * 입장 경로(§10):
 *  - 세션에 자격(토큰)이 있으면(코드 입장 후 이동) 그대로 접속
 *  - 직통 링크 `/v/{room}#k=<view_key>` — opt-in 공개 방 한정(§10.2).
 *    fragment 는 서버 로그에 남지 않으며, 읽는 즉시 주소창에서 지운다.
 *    그 비밀은 `/api/rooms/{room}/link` 에서 viewer 토큰으로 **교환**한다
 *    (sec SEC-03 후속 — 구 view_key 를 소켓 자격으로 쓰지 않는다).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { Badge } from '@cupia/ui/badge'
import { Button } from '@cupia/ui/button'
import { Empty } from '@cupia/ui/empty'
import { Alert, AlertDescription, AlertTitle } from '@cupia/ui/alert'
import { Spinner } from '@cupia/ui/spinner'
import { confirm } from '@cupia/ui/confirm-dialog'
import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  ChevronsDownUp,
  ChevronsUpDown,
  Columns2,
  Eye,
  EyeOff,
  KeyRound,
  LogOut,
  Maximize2,
  MonitorSmartphone,
  MonitorX,
} from 'lucide-react'
import { Brand } from '../components/brand'
import { CaptionFeed } from '../components/caption-feed'
import { SplitFeed } from '../components/split-feed'
import { SplitRows } from '../components/split-rows'
import { useSplitRowsFallback } from '../components/split-feed'
import { ChatPanel } from '../components/chat-panel'
import { AssistDrawer } from '../components/assist-drawer'
import { StatusAlerts } from '../components/status-alerts'
import { LagBadge, LiveBadge } from '../components/badges'
import { ThemeControls } from '../components/theme-controls'
import { TranscriptDownload } from '../components/transcript-download'
import { HeaderToolsToggle } from '../components/header-tools-toggle'
import { useHeaderTools } from '../lib/use-header-tools'
import { useRoomStream } from '../lib/use-room-stream'
import {
  acquireWakeLock,
  armGestureAcquire,
  releaseWakeLock,
  setWakeLockWanted,
  subscribeWakeLockStatus,
  useWakeLockPref,
  watchVisibilityReacquire,
  type WakeLockStatus,
} from '../lib/wake-lock'
import { useFeedOrder } from '../lib/use-feed-order'
import { useSplitView } from '../lib/use-split-view'
import { CoachPanel } from '../components/coach-panel'
import { useSourceVeil } from '../lib/use-source-veil'
import { useCollapseInferred } from '../lib/use-collapse-inferred'
import {
  hasAssistantJudgeItems,
  useCollapseAssistant,
} from '../lib/use-collapse-assistant'
import { useCaptionScale } from '../lib/use-caption-scale'
import { PRESENTATION_STEP } from '../lib/caption-scale'
import { useServerConfig } from '../lib/use-server-config'
import {
  loadCredential,
  saveCredential,
  takeKeyFromFragment,
  type RoomCredential,
} from '../lib/session'
import { createRoomAuth } from '../lib/token'
import { ApiError, exchangeLinkKey, requestAssist } from '../lib/api'
import { directionLabel } from '../lib/room-info'

type EntryState =
  | { status: 'resolving' }
  /**
   * 자격 전체를 들고 간다 — 토큰 수명주기(lib/token.ts)가 cred 를 쓴다.
   * `gesture` — 이 화면에 오기까지 사용자 제스처(코드 입력 제출)가 있었는지.
   * 직통 링크 자동 입장은 제스처가 없어 화면 켜짐 유지를 바로 요청할 수
   * 없다(lib/wake-lock.ts armGestureAcquire 참고).
   */
  | { status: 'ready'; cred: RoomCredential; gesture: boolean }
  | { status: 'denied'; message: string }

export function ViewerRoute(): React.ReactElement {
  const params = useParams<{ room: string }>()
  const room = params.room ?? ''
  const [entry, setEntry] = useState<EntryState>({ status: 'resolving' })

  // 직통 링크 fragment 는 마운트 시 딱 한 번 읽는다(읽는 즉시 URL 에서 제거).
  const fragmentKey = useMemo(() => takeKeyFromFragment(), [])

  useEffect(() => {
    if (!room) {
      setEntry({ status: 'denied', message: '방 주소가 올바르지 않습니다.' })
      return
    }

    const stored = loadCredential(room)

    // 직통 링크 — opt-in 공개 방만 허용된다(§10.2).
    //
    // fragment 의 비밀을 `/link` 에 내밀어 **viewer 토큰으로 교환**한다.
    // 서버는 없는 방·닫힌 방·비공개 방·틀린 비밀을 전부 uniform 403 으로
    // 답하므로(존재 미누출) 화면도 한 문구로 묶는다. 응답의 언어·이름은
    // 방의 실제 값이다(§W3 — 리터럴 기본값 금지).
    //
    // `fallback` — 저장된 자격이 있으면 교환 실패가 곧 거절은 아니다
    // (패스코드로 들어온 탭에서 링크를 다시 연 경우 등): 저장 자격으로
    // 이어 간다. 없으면 한 문구로 거절.
    const exchange = (
      secret: string, gesture: boolean, fallback: RoomCredential | null,
    ): void => {
      exchangeLinkKey(room, secret)
        .then((info) => {
          const cred: RoomCredential = {
            ...(fallback ?? {}),
            room, key: '', role: 'viewer',
            token: info.token,
            tokenExp: Date.now() + (info.token_ttl_s ?? 600) * 1000,
            // 갱신은 같은 비밀로 같은 경로를 다시 지난다(lib/token.ts).
            linkKey: secret,
            src: info.src, dst: info.dst,
            bidirectional: info.bidirectional ?? false,
            name: info.name, topic: info.topic,
          }
          saveCredential(cred)
          setEntry({ status: 'ready', cred, gesture })
        })
        .catch((err: unknown) => {
          if (fallback) {
            setEntry({ status: 'ready', cred: fallback, gesture: true })
            return
          }
          setEntry({
            status: 'denied',
            message:
              err instanceof ApiError && err.status === 403
                ? '이 링크로는 입장할 수 없습니다. 6자리 코드로 입장해 주세요.'
                : err instanceof ApiError
                  ? err.message
                  : '입장할 수 없습니다. 코드로 입장해 주세요.',
          })
        })
    }

    // 1) 링크를 (다시) 열었다 — 저장 자격이 있어도 **교환한다**. 같은 탭에서
    //    링크를 다시 여는 것이 옛 자격을 새 토큰으로 이행시키는 손잡이다
    //    (TASK_sec_fix_e #2). 저장 자격은 교환 실패 시의 폴백이다.
    if (fragmentKey) {
      exchange(fragmentKey, false, stored)
      return
    }

    if (stored) {
      // 2) 옛 번들이 저장한 직통 링크 자격 — 재교환 비밀(linkKey)이 없고
      //    구 view_key(`key`)만 있다. 그 값이 곧 `/link` 교환 입력이므로
      //    지금 교환해 토큰 + linkKey 로 이행한다. 교환이 거절되면(방이
      //    비공개로 바뀜 등) 저장 자격 그대로 간다 — 서버가 구 키를 아직
      //    받는 형상이면 그걸로 이어지고, 아니면 transport 가 말한다.
      if (stored.role === 'viewer' && stored.key && !stored.linkKey && !stored.passcode) {
        exchange(stored.key, true, stored)
        return
      }
      // 3) 코드 입장 등으로 이미 자격이 있으면 그대로 쓴다.
      setEntry({ status: 'ready', cred: stored, gesture: true })
      return
    }

    setEntry({
      status: 'denied',
      message: '입장 자격이 없습니다. 6자리 코드로 입장해 주세요.',
    })
  }, [room, fragmentKey])

  if (entry.status === 'resolving') {
    return (
      <div className="flex min-h-screen items-center justify-center gap-3 text-[var(--color-muted-foreground)]">
        <Spinner className="h-4 w-4" />
        입장 확인 중…
      </div>
    )
  }

  if (entry.status === 'denied') {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Empty
          icon={<KeyRound className="h-6 w-6" />}
          title="입장할 수 없습니다"
          description={entry.message}
          action={
            <Button asChild>
              <Link to="/">코드로 입장하기</Link>
            </Button>
          }
        />
      </div>
    )
  }

  return <ViewerScreen room={room} cred={entry.cred} gesture={entry.gesture} />
}

function ViewerScreen({
  room,
  cred,
  gesture,
}: {
  room: string
  cred: RoomCredential
  gesture: boolean
}): React.ReactElement {
  const { src, dst, name } = cred
  const navigate = useNavigate()
  // §5.2 방 자격 수명주기 — viewer 토큰, 만료 전 패스코드(또는 직통 링크
  // 비밀) 재교환(stream 소유 경로라 auth 다운에도 갱신된다).
  const auth = useMemo(() => createRoomAuth(cred), [cred])
  const { state, conn, loadMore, sendChat } = useRoomStream({
    room,
    key: cred.key,
    role: 'viewer',
    auth,
  })
  const scale = useCaptionScale()
  // "발표 크기 이상" 이면 카드 간격을 넓히고 원문 줄을 접는다 — 큰 글씨로
  // 멀리서 볼 때는 번역 한 줄이 주인공이다(§14).
  const presentation = scale.step >= PRESENTATION_STEP
  // 뷰어의 정렬 취향은 호스트와 **독립**이다(같은 사람이 두 역할을 오간다).
  const { order, toggle: toggleOrder } = useFeedOrder('viewer')
  // 분할 보기(D1) — 정렬과 독립, 뷰어·호스트 각자 기억한다.
  const { mode: splitMode, toggle: toggleSplit } = useSplitView('viewer')
  /** D3-4 — `rows` 는 ≤640px 에서 v4 탭 계약으로 폴백한다(상태 변경 아님). */
  const splitRowsFallback = useSplitRowsFallback()
  // 원문 가림 — **참가자도 각자** 자기 화면을 가릴 수 있다(시각만,
  // lib/source-veil.ts). 호스트와 독립 저장이다.
  const veil = useSourceVeil('viewer')
  // §6.5-7 유령(추론) 자막 접기 — 원문 가림과 다른 축이라 별도 저장 키를 쓴다.
  const collapseInferred = useCollapseInferred()
  // 판정기 자막 접기 — 추론 접기와 **별도 축**(저장 키도 다르다). 기본 off.
  const collapseAssistant = useCollapseAssistant()
  /** 판정기 배지가 이 방에서 실제로 도는가 — 손잡이를 세울지의 판정(T3). */
  const judgeBadgeLive = useMemo(
    () => hasAssistantJudgeItems(state.items),
    [state.items],
  )
  // 화면 켜짐 유지 — ViewerScreen 은 entry.status === 'ready' 일 때만
  // 마운트되므로, 마운트 자체가 "방 참여" 시점이다(lib/wake-lock.ts).
  //
  // 코드 입장(gesture=true)은 그 제출 자체가 사용자 제스처이므로 바로
  // 요청한다. 직통 링크 자동 입장(gesture=false)은 제스처가 없어 브라우저가
  // 거부할 수 있다 — 문서에서 첫 터치/클릭이 올 때까지 기다린다(그동안
  // 안내를 보여준다).
  const wakeLockPref = useWakeLockPref()
  const [needsGesture, setNeedsGesture] = useState(!gesture)
  const [wakeLockStatus, setWakeLockStatus] = useState<WakeLockStatus>('released')
  // 연결이 끝난 방(나가기·종료)에서는 화면이 계속 켜져 있을 이유가 없다 —
  // 언마운트 없이도(뷰가 종료 상태를 그대로 보여주는 동안) 놓는다.
  const connTerminal =
    conn === 'room_gone' || conn === 'room_closed' || conn === 'terminated'
  useEffect(() => {
    if (!wakeLockPref.enabled) {
      // 선호 off 는 "원하지 않음" 이다 — 게이트를 내려 재요청 경로까지 막는다.
      setWakeLockWanted(false)
      void releaseWakeLock()
      return
    }
    // 종결된 방에서는 선호를 다시 켜도 잠금을 집지 않는다 — 게이트는
    // "참여 중 ∧ 선호 on" 이고, 종결이면 참여 중이 아니다(CORRECTION #3 P2′).
    // 호스트의 roomGone 가드(host.tsx)와 같은 규율이다.
    if (connTerminal) return
    // 참여 중 ∧ 선호 on = 잠금을 원한다(CORRECTION #2 P2 게이트).
    // StrictMode 이중 마운트에서 첫 cleanup 이 게이트를 내리므로, 모든
    // 마운트가 여기서 다시 올린다(멱등 — CORRECTION #3 P3).
    setWakeLockWanted(true)
    if (gesture) {
      void acquireWakeLock().then(setWakeLockStatus)
      return () => {
        setWakeLockWanted(false)
        void releaseWakeLock()
      }
    }
    setNeedsGesture(true)
    const cleanup = armGestureAcquire(setWakeLockStatus)
    return () => {
      cleanup()
      setWakeLockWanted(false)
      void releaseWakeLock()
    }
  }, [wakeLockPref.enabled, gesture, connTerminal])
  useEffect(() => watchVisibilityReacquire(), [])
  // 브라우저發 release(예: OS 정책)를 상태에 반영한다(CORRECTION #1 SHOULD 4).
  useEffect(() => subscribeWakeLockStatus(setWakeLockStatus), [])
  // 첫 제스처가 오면 안내를 지운다 — armGestureAcquire 는 wake-lock.ts 안에서
  // 상태를 들고 있으므로, 같은 이벤트를 여기서도 한 번 들어 안내만 끈다.
  useEffect(() => {
    if (gesture || !needsGesture) return
    const onGesture = (): void => setNeedsGesture(false)
    document.addEventListener('pointerdown', onGesture, { once: true })
    document.addEventListener('touchstart', onGesture, { once: true })
    document.addEventListener('click', onGesture, { once: true })
    return () => {
      document.removeEventListener('pointerdown', onGesture)
      document.removeEventListener('touchstart', onGesture)
      document.removeEventListener('click', onGesture)
    }
  }, [gesture, needsGesture])
  useEffect(() => {
    if (!connTerminal) return
    // 종결 화면은 다시 잠금을 집지 않는다 — 게이트를 먼저 내린다.
    setWakeLockWanted(false)
    void releaseWakeLock()
  }, [connTerminal])
  const serverConfig = useServerConfig()
  // 휴대폰 머리글 도구줄 접기/펼치기 — 뷰어·호스트 각자 기억한다.
  const tools = useHeaderTools('viewer')

  /* ── 어시스트 질의(D1/D2) — 참가자도 물을 수 있다 ────────────────────
   *
   * 답은 REST 가 아니라 `assist` 이벤트로 소켓에 온다(호스트와 같은 축).
   * 여기서 하는 일은 **발사와 거절 표면화**뿐이다 — 거절을 삼키면 참가자는
   * 답이 오는 줄 알고 기다린다(§1 조용한 실패 금지).
   */
  const [assistPending, setAssistPending] = useState(false)
  const askAssist = useCallback(
    async (q: string): Promise<void> => {
      setAssistPending(true)
      try {
        await requestAssist(room, auth.current(), q)
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 429) {
          throw new Error(
            err.code === 'assist_busy'
              ? // 호스트 문구("이전 질문에…")를 재사용하지 않는다: 참가자는
                // 자기가 묻지 않았는데도 busy 를 맞을 수 있어 거짓말이 된다.
                '다른 참가자의 질문에 답하는 중입니다. 잠시 후 다시 시도해 주세요.'
              : '질문이 너무 잦습니다. 잠시 후 다시 시도해 주세요.',
          )
        }
        if (err instanceof ApiError && err.status === 503) {
          throw new Error('통역이 밀려 있어 잠시 뒤에 답할 수 있습니다.')
        }
        throw new Error('질문 요청이 실패했습니다.')
      } finally {
        setAssistPending(false)
      }
    },
    [room, auth],
  )
  // §11.4 회의록 저장 결과 알림 — 실패를 조용히 삼키지 않는다.
  const [toast, setToast] = useState<string | null>(null)
  const notify = useCallback((message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 3200)
  }, [])

  const goFullscreen = useCallback(() => {
    const el = document.documentElement
    if (document.fullscreenElement) void document.exitFullscreen()
    else void el.requestFullscreen().catch(() => undefined)
  }, [])

  /** 참가자 이탈 — 서버 호출 없음(언마운트가 연결을 닫는다, use-room-stream.ts:187) */
  const leaveRoom = useCallback(async () => {
    const ok = await confirm({
      title: '회의실에서 나갈까요?',
      description:
        '첫 화면으로 돌아갑니다. 회의는 계속 진행되고, 같은 링크로 다시 들어올 수 있습니다.',
      confirmLabel: '나가기',
      cancelLabel: '취소',
    })
    if (!ok) return
    void navigate('/')
  }, [navigate])

  return (
    <div className="flex h-screen flex-col bg-[var(--color-background)]">
      {/* 휴대폰(≤640px)에서는 머리글을 한 줄(마크·방 코드·상태·방향·[도구▾])로
          줄이고 도구줄은 기본 접힘이다(lib/use-header-tools.ts) — 실측
          227px/844px(40%)이 자막 자리를 먹고 있었다. 넓은 화면은 현행 그대로. */}
      <header
        className={`flex flex-wrap items-center border-b border-[var(--color-border)] ${
          tools.narrow ? 'gap-x-1.5 gap-y-1 px-3 py-1.5' : 'gap-2 px-4 py-2.5'
        }`}
      >
        {/* 부제는 생략한다 — 방 배지·상태가 같은 줄을 쓰므로 락업만으로
            읽히는 편이 낫다. */}
        <Brand compact={tools.narrow} />
        {/* S28 회의 이름 — 없으면(구 방·미기재) 기존 렌더 그대로 둔다.
            휴대폰에서는 도구줄(펼침)에 둔다 — 한 줄 예산에 들어가지 않는다. */}
        {name && !tools.narrow && <span className="font-medium">{name}</span>}
        <Badge variant="code">{room}</Badge>
        <LiveBadge room={state.room} />
        <LagBadge room={state.room} />
        <Badge variant="softGray" title="현재 통역 방향">
          {directionLabel(src, dst)}
        </Badge>
        {tools.narrow && (
          <HeaderToolsToggle
            className="ms-auto"
            expanded={tools.expanded}
            onToggle={tools.toggle}
            controls="viewer-header-tools"
          />
        )}

        {/* 좁은 화면에서는 줄바꿈한다 — 감싸지 않으면 오른쪽 항목이 화면 밖으로
            밀려 누를 수 없게 된다(호스트 헤더와 같은 규율). 휴대폰에서는
            펼쳤을 때만 그리고, 머리글 줄 아래에 통째로 선다(basis-full). */}
        {tools.showTools && (
        <div
          id="viewer-header-tools"
          data-testid="viewer-header-tools"
          className={`flex flex-wrap items-center gap-1 sm:ms-auto ${tools.narrow ? 'basis-full' : ''}`}
        >
          {name && tools.narrow && <span className="font-medium">{name}</span>}
          {/* §11.4 참가자도 자기가 본 회의록을 받는다(범위는 서버가 정한다) */}
          <TranscriptDownload room={room} authKey={auth.current()} variant="ghost"
            disabled={conn === 'room_gone' || conn === 'room_closed' || conn === 'terminated'}
            onNotify={notify} />
          <Button variant="ghost" size="sm" onClick={goFullscreen} title="전체 화면">
            <Maximize2 className="h-4 w-4" />
            전체 화면
          </Button>
          {/* 원문 가림 — 뷰어에는 설정 서랍이 없으므로 헤더에 바로 둔다.
              버튼은 누르면 무엇이 되는지 적고(정렬 버튼과 같은 규율), 가려진
              동안은 표면(variant)·aria-pressed 로 상태를 말한다 — 가려 둔
              것을 잊지 않게. 가림은 화면뿐이고 회의록에는 원문이 남는다. */}
          <Button
            variant={veil.veiled ? 'secondary' : 'ghost'}
            size="sm"
            onClick={veil.toggle}
            aria-pressed={veil.veiled}
            data-testid="veil-toggle"
            title={
              veil.veiled
                ? '가려 둔 원문(들린 내용)을 다시 표시합니다'
                : '원문(들린 내용)을 내 화면에서만 가립니다 — 회의록에는 남습니다'
            }
          >
            {veil.veiled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {/* 라벨 스왑으로 버튼 폭이 변하면 좁은 화면에서 이웃 버튼이 밀린다
                (QA P3 실측 12px@360px) — 두 라벨을 같은 칸에 겹쳐 폭을 항상
                긴 쪽으로 고정한다. invisible(visibility:hidden)은 접근성
                트리에서도 빠져 스크린리더는 보이는 라벨만 읽는다. */}
            <span className="grid text-start">
              <span className={`col-start-1 row-start-1 ${veil.veiled ? 'invisible' : ''}`}>
                원문 가리기
              </span>
              <span className={`col-start-1 row-start-1 ${veil.veiled ? '' : 'invisible'}`}>
                원문 표시
              </span>
            </span>
          </Button>
          {/* 화면 켜짐 유지 — 뷰어에는 설정 서랍이 없으므로 헤더에 바로 둔다
              (원문 가림과 같은 규율). */}
          <Button
            variant={wakeLockPref.enabled ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => wakeLockPref.setEnabled(!wakeLockPref.enabled)}
            aria-pressed={wakeLockPref.enabled}
            data-testid="wake-lock-toggle"
            title={
              wakeLockPref.enabled
                ? '화면 켜짐 유지를 끕니다'
                : '화면이 꺼지지 않게 유지합니다'
            }
          >
            <MonitorSmartphone className="h-4 w-4" />
          </Button>
          {/* 직통 링크 자동 입장은 제스처가 없어 wakeLock 요청을 아직
              못 보냈다 — 첫 터치까지 기다린다는 것을 알린다. */}
          {wakeLockPref.enabled && needsGesture && (
            <span className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]">
              화면 켜짐 유지를 위해 한 번 터치해 주세요
            </span>
          )}
          {/* 취향은 켰지만 실제로 걸리지 않은 상태(미지원·거부)는 말해야
              한다 — 화면이 꺼져도 사용자가 이유를 알 수 있게(CORRECTION #1
              SHOULD 3). */}
          {wakeLockPref.enabled && !needsGesture
            && (wakeLockStatus === 'unsupported' || wakeLockStatus === 'denied') && (
            <span
              className="text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
              data-testid="wake-lock-status"
            >
              {wakeLockStatus === 'unsupported'
                ? '이 브라우저는 화면 켜짐 유지를 지원하지 않습니다'
                : '화면 켜짐 유지 요청이 거부되었습니다'}
            </span>
          )}
          {/* 추론(유령) 자막 접기 — 원문 가림과 나란히, 같은 라벨 스왑 규율. */}
          <Button
            variant={collapseInferred.collapsed ? 'secondary' : 'ghost'}
            size="sm"
            onClick={collapseInferred.toggle}
            aria-pressed={collapseInferred.collapsed}
            data-testid="collapse-inferred-toggle"
            title={
              collapseInferred.collapsed
                ? '지어낸 것으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다 — 지금은 접힌 상태입니다.'
                : '지어낸 것으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다.'
            }
          >
            {collapseInferred.collapsed ? (
              <ChevronsUpDown className="h-4 w-4" />
            ) : (
              <ChevronsDownUp className="h-4 w-4" />
            )}
            <span className="grid text-start">
              <span
                className={`col-start-1 row-start-1 ${collapseInferred.collapsed ? 'invisible' : ''}`}
              >
                추론 접기
              </span>
              <span
                className={`col-start-1 row-start-1 ${collapseInferred.collapsed ? '' : 'invisible'}`}
              >
                추론 펼치기
              </span>
            </span>
          </Button>
          {/* 판정기 자막 접기 — 추론 접기와 나란히, 같은 라벨 스왑 규율.
              **배지가 실제로 도는 방에서만** 손잡이가 선다(T3): 배지 없이
              접히면 "이유 없이 사라진 줄" 이 되고, 판정기가 꺼진 방에 손잡이만
              서면 그것이 거짓 손잡이다(`main.py:858` 과 같은 규율). */}
          {judgeBadgeLive && (
            <Button
              variant={collapseAssistant.collapsed ? 'secondary' : 'ghost'}
              size="sm"
              aria-pressed={collapseAssistant.collapsed}
              data-testid="collapse-assistant-toggle"
              onClick={collapseAssistant.toggle}
              title={
                collapseAssistant.collapsed
                  ? '통역기 답변으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다 — 지금은 접힌 상태입니다.'
                  : '통역기 답변으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다.'
              }
            >
              {collapseAssistant.collapsed ? (
                <ChevronsUpDown className="h-4 w-4" />
              ) : (
                <ChevronsDownUp className="h-4 w-4" />
              )}
              <span className="grid text-start">
                <span
                  className={`col-start-1 row-start-1 ${collapseAssistant.collapsed ? 'invisible' : ''}`}
                >
                  답변 접기
                </span>
                <span
                  className={`col-start-1 row-start-1 ${collapseAssistant.collapsed ? '' : 'invisible'}`}
                >
                  답변 펼치기
                </span>
              </span>
            </Button>
          )}
          {/* 정렬 방향 — 버튼은 **누르면 무엇이 되는지** 적는다(§14.1 방향
              버튼과 같은 규율). 지금 상태를 적으면 눌러야 할지 알 수 없다. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleOrder}
            data-testid="order-toggle"
            title={
              order === 'newest-first'
                ? '최신 자막을 아래에 표시합니다'
                : '최신 자막을 위에 표시합니다'
            }
          >
            {order === 'newest-first' ? (
              <ArrowDownWideNarrow className="h-4 w-4" />
            ) : (
              <ArrowUpWideNarrow className="h-4 w-4" />
            )}
            최신 {order === 'newest-first' ? '아래로' : '위로'}
          </Button>
          {/* 분할 보기(D1) — 발표 모드에서도 유효하다(D1-6).
              ⚠️ 라벨은 **지금 상태**다(다음 동작이 아니다). 뷰어 기본이
              나란히 보기라(split-view.ts `defaultSplitMode`) "다음 동작"
              라벨을 쓰면 분할로 들어온 참가자가 '통합 보기' 를 보고 지금이
              통합인 줄 안다. `aria-pressed` 와 라벨이 같은 것을 가리키게
              둔다. v5 에서 켜진 상태는 `'split'`(v4 2열)과 `'rows'`(행 정렬)
              **둘 다**이므로 판정은 "통합이 아닌가" 로 본다. */}
          <Button
            variant={splitMode !== 'unified' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={toggleSplit}
            data-testid="split-toggle"
            aria-pressed={splitMode !== 'unified'}
            title={
              splitMode !== 'unified'
                ? '분할 보기 모드입니다 — 눌러서 통합 보기로'
                : '통합 보기 모드입니다 — 눌러서 분할 보기로'
            }
          >
            <Columns2 className="h-4 w-4" />
            {splitMode !== 'unified' ? '분할 보기 모드' : '통합 보기'}
          </Button>
          {/* 자막 크기 +/− 는 ThemeControls 안의 스테퍼가 담당한다 */}
          <ThemeControls showPresentation />
          {/* 나가기 — 파괴적 조작은 오른쪽 끝(호스트 헤더와 같은 규율).
              재입장 가능하므로 destructive 표면·자격 삭제는 쓰지 않는다. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={leaveRoom}
            data-testid="viewer-leave"
            title="이 회의실에서 나가고 첫 화면으로 돌아갑니다"
          >
            <LogOut className="h-4 w-4" />
            나가기
          </Button>
        </div>
        )}
      </header>

      <div className="px-4 pt-3">
        <StatusAlerts room={state.room} conn={conn} viewerRole="viewer"
          credentialDead={auth.status() === 'credential_dead'} />
      </div>

      {/* §10.1 뷰어 격리는 유지된다 — skip·utt.state·지연/원장은 여전히
          이 소켓으로 오지 않는다. 코치만 **방이 공개로 설정된 경우**
          vis=all 로 온다(§7.2.2). 발표 모드를 해치지 않도록 기본 접힘. */}
      <main className="min-h-0 flex-1 px-4 py-3">
        {/* 뷰어는 자막 전용 화면이라 더 넓게 쓴다(발표 모드 전제). */}
        <div className="mx-auto h-full w-full max-w-[min(100%,88rem)]">
          {/* 3분기(SPLITVIEW_ROWS_DESIGN D3-1·D3-4) — `rows` 는 데스크톱에서만
              행 정렬이고, ≤640px 에서는 v4 탭 계약(`SplitFeed`)으로 폴백한다.
              폴백은 **렌더 시점 판정**이지 상태 변경이 아니다: 저장값은
              `rows` 로 남아 폭이 돌아오면 행으로 복귀한다. */}
          {splitMode === 'rows' && !splitRowsFallback ? (
            <SplitRows
              items={state.items}
              src={src}
              dst={dst}
              showSource={!presentation}
              veilSource={veil.veiled}
              collapseInferred={collapseInferred.collapsed}
              collapseAssistant={collapseAssistant.collapsed}
              onLoadMore={loadMore}
              presentation={presentation}
              order={order}
              emptyTitle="발표가 시작되기를 기다리는 중"
              emptyDescription="호스트가 말하기 시작하면 자막이 여기에 실시간으로 표시됩니다."
              room={room}
              viewerKey={auth.current()}
            />
          ) : splitMode !== 'unified' ? (
            <SplitFeed
              items={state.items}
              src={src}
              dst={dst}
              showSource={!presentation}
              veilSource={veil.veiled}
              collapseInferred={collapseInferred.collapsed}
              collapseAssistant={collapseAssistant.collapsed}
              onLoadMore={loadMore}
              presentation={presentation}
              order={order}
              conn={conn}
              liveEmptyTitle="발표가 시작되기를 기다리는 중"
              liveEmptyDescription="호스트가 말하기 시작하면 자막이 여기에 실시간으로 표시됩니다."
              room={room}
              viewerKey={auth.current()}
            />
          ) : (
            <CaptionFeed
              items={state.items}
              src={src}
              dst={dst}
              showSource={!presentation}
              veilSource={veil.veiled}
              collapseInferred={collapseInferred.collapsed}
              collapseAssistant={collapseAssistant.collapsed}
              onLoadMore={loadMore}
              presentation={presentation}
              order={order}
              conn={conn}
              emptyTitle="발표가 시작되기를 기다리는 중"
              emptyDescription="호스트가 말하기 시작하면 자막이 여기에 실시간으로 표시됩니다."
              room={room}
              viewerKey={auth.current()}
            />
          )}
        </div>
      </main>

      {/* §7.2.2 코치 — 방이 공개로 켠 경우에만 항목이 온다. 항목이 하나도
          없으면 손잡이도 그리지 않는다(빈 패널을 두지 않는다). */}
      {state.coach.length > 0 && (
        <div className="border-t border-[var(--color-border)] px-4 py-2">
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center gap-2 text-(length:--text-sm-size) text-[var(--color-muted-foreground)]">
              <span className="font-medium">AI 코치</span>
              <span className="tabular-nums">{state.coach.length}</span>
              <span className="ms-auto text-(length:--text-xs-size) group-open:hidden">
                펼치기
              </span>
              <span className="ms-auto hidden text-(length:--text-xs-size) group-open:inline">
                접기
              </span>
            </summary>
            <div className="mt-2 max-h-[40vh] overflow-y-auto" data-testid="viewer-coach">
              <CoachPanel
                items={state.coach}
                room={state.room}
                enabled
                order={order}
              />
            </div>
          </details>
        </div>
      )}

      {/* D1/D4 참가자 질문 — 서버가 켠 경우에만 그린다. 방 옵트인
          (`assist_public`)은 **답의 가시성** 축이지 질문 권한이 아니다. */}
      {serverConfig.assist && (
        <AssistDrawer
          items={state.assist}
          onAsk={askAssist}
          pending={assistPending}
          hidden={presentation}
        />
      )}

      {/* §8.5 회의 중 채팅 — 서버가 켠 경우에만 그린다. 꺼져 있으면
          버튼 자체가 없다(눌러도 안 되는 버튼을 두지 않는다). */}
      {serverConfig.chat && (
        <ChatPanel
          chat={state.chat}
          onSend={sendChat}
          error={state.chatError}
          disabled={conn === 'room_gone' || conn === 'room_closed'
            || conn === 'terminated' || state.room.state === 'closed'}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-[var(--radius-lg)] bg-[var(--color-foreground)] px-4 py-2 text-(length:--text-sm-size) text-[var(--color-background)] shadow-lg"
        >
          {toast}
        </div>
      )}

      {state.room.state === 'closed' && (
        <div className="px-4 pb-4">
          <Alert variant="default">
            <MonitorX className="h-4 w-4" />
            <AlertTitle>회의가 종료되었습니다</AlertTitle>
            <AlertDescription>호스트가 방을 닫았습니다.</AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  )
}
